/**
 * Posture Scan API Integration
 * 
 * Handles communication with the Watch Your Health Posture Scan API
 */

import { PostureScanAPIRequest, PostureScanAPIResponse } from '../types';
import { POSTURE_SCAN_API_URL, POSTURE_SCAN_API_KEY, isPostureScanConfigured } from '../config';

export class PostureScanAPIError extends Error {
  constructor(
    message: string,
    public statusCode?: number,
    public details?: any
  ) {
    super(message);
    this.name = 'PostureScanAPIError';
  }
}

/**
 * Submit posture scan to the API
 * @param frontImageBase64 - Base64 encoded front image (without data-url prefix)
 * @param sideImageBase64 - Base64 encoded side image (without data-url prefix)
 * @param abortSignal - Optional AbortSignal for request cancellation
 * @returns Posture scan results
 */
export async function submitPostureScan(
  frontImageBase64: string,
  sideImageBase64: string,
  abortSignal?: AbortSignal
): Promise<PostureScanAPIResponse> {
  // Check if API is configured
  if (!isPostureScanConfigured()) {
    throw new PostureScanAPIError(
      'API key is missing or invalid. Please configure REACT_APP_WYH_POSTURE_API_KEY in your .env file.',
      401
    );
  }

  // Validate inputs
  if (!frontImageBase64 || !sideImageBase64) {
    throw new PostureScanAPIError('Both front and side images are required', 400);
  }

  // Prepare request payload
  const requestPayload: PostureScanAPIRequest = {
    front_image: frontImageBase64,
    side_image: sideImageBase64,
  };

  console.log('=== POSTURE SCAN API REQUEST ===');
  console.log('API URL:', POSTURE_SCAN_API_URL);
  console.log('Front image length:', frontImageBase64.length);
  console.log('Side image length:', sideImageBase64.length);

  try {
    const response = await fetch(POSTURE_SCAN_API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': POSTURE_SCAN_API_KEY || '',
      },
      body: JSON.stringify(requestPayload),
      signal: abortSignal,
    });

    console.log('Response Status:', response.status);
    console.log('Response OK:', response.ok);

    // Handle error responses
    if (!response.ok) {
      const errorText = await response.text();
      console.error('API Error Response:', errorText);

      let errorMessage = 'Scan failed. Please try again.';
      
      if (response.status === 400) {
        errorMessage = 'Invalid images or missing images. Please re-upload and try again.';
      } else if (response.status === 401) {
        errorMessage = 'API key invalid or missing. Check environment configuration.';
      } else if (response.status >= 500) {
        errorMessage = 'Server error. Please try again later.';
      }

      throw new PostureScanAPIError(errorMessage, response.status, errorText);
    }

    // Parse response
    const responseText = await response.text();
    console.log('Raw Response:', responseText);

    let result: PostureScanAPIResponse;
    try {
      result = JSON.parse(responseText);
      console.log('Parsed Response:', result);
    } catch (parseError) {
      console.error('Failed to parse response:', parseError);
      throw new PostureScanAPIError('Invalid response format from API', 500);
    }

    // Validate response structure
    if (!result.front_view || !result.side_view || !result.request_id) {
      console.error('Invalid response structure:', result);
      throw new PostureScanAPIError('Invalid response structure from API', 500);
    }

    console.log('=== POSTURE SCAN API SUCCESS ===');
    return result;

  } catch (error: any) {
    // Handle AbortError
    if (error.name === 'AbortError') {
      console.log('Request was aborted');
      throw new PostureScanAPIError('Request was cancelled', 0);
    }

    // Re-throw PostureScanAPIError
    if (error instanceof PostureScanAPIError) {
      throw error;
    }

    // Handle network errors
    console.error('Network or unexpected error:', error);
    throw new PostureScanAPIError(
      'Network error. Please check your connection and try again.',
      0,
      error
    );
  }
}

/**
 * Log posture scan results to webhook for ETL/analytics
 * @param userData - User information
 * @param scanResults - Scan results from API
 * @param crmClientId - Optional CRM client ID from URL params
 * @returns Promise<void>
 */
export async function logPostureScanResults(
  userData: { userName: string; phoneNumber: string; emailId: string },
  scanResults: PostureScanAPIResponse,
  crmClientId: string | null = null
): Promise<void> {
  const webhookUrl = 'https://etl.futurisk.in/webhook/fee3ecb4-f2bb-4955-8f29-7bb4715cd6c3';

  const payload = {
    user_data: {
      name: userData.userName,
      phone: userData.phoneNumber,
      email: userData.emailId,
      crmClientId: crmClientId,
      scan_date: new Date().toISOString(),
    },
    scan_results: {
      front_view: {
        score: scanResults.front_view.score,
        analysis: scanResults.front_view.analysis,
        improvements: scanResults.front_view.improvements,
      },
      side_view: {
        score: scanResults.side_view.score,
        analysis: scanResults.side_view.analysis,
        improvements: scanResults.side_view.improvements,
      },
      timestamp: scanResults.timestamp,
      request_id: scanResults.request_id,
    },
  };

  console.log('=== LOGGING TO WEBHOOK ===');
  console.log('Webhook URL:', webhookUrl);
  console.log('Payload:', payload);

  try {
    const response = await fetch(webhookUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    console.log('Webhook Response Status:', response.status);
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error('Webhook logging failed:', errorText);
      // Don't throw error - logging is non-critical
    } else {
      console.log('✓ Results logged to webhook successfully');
    }
  } catch (error) {
    console.error('Error logging to webhook:', error);
    // Don't throw error - logging is non-critical, shouldn't break user flow
  }
}
